// T8-BOARD-NUMBERS: the in-memory API's board Numbers tab (services/api/src/kpi/board-numbers.ts)
// with the real route's rules: counts, tasks per status and done per week from the work twin's
// tasks; each number of the board counted live from the same tasks (no snapshot job here) in
// UTC Monday weeks and calendar months; a person who edits the board adds and removes numbers, a
// client viewer is refused (not a work member). Simpler than the server: no restricted tasks,
// field audiences, tags or computed fields, and numbers live apart from the KPI fakes.
// No demo rows: every board starts without numbers.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";

type Numbers = OperationOutput<"boards.numbers">;
type Tile = Numbers["tiles"][number];
type Create = OperationInput<"boards.addNumber">["body"];
type Cadence = Tile["cadence"];

type TaskLite = {
  id: string;
  board_id: string;
  status: string;
  created_at: string;
  due_at: string | null;
  completed_at: string | null;
  fields: Record<string, unknown>;
  deleted?: boolean;
};
type BoardLite = { id: string; status_set: { key: string; name: string; done?: boolean }[] };
type FieldLite = { id: string; board_id: string; name: string; type: string };

export type BoardNumberDeps = {
  now: () => Date;
  boardsOf: (companyId: string) => BoardLite[];
  tasksOf: (companyId: string) => TaskLite[];
  fieldsOf: (companyId: string) => FieldLite[];
};

type Stored = {
  id: string;
  key: string;
  name: string;
  unit: Tile["unit"];
  direction: Tile["direction"];
  cadence: Cadence;
  task_query: Tile["task_query"];
};

const DAY = 86_400_000;
const NUMERIC = new Set(["number", "currency", "checkbox", "formula", "rollup"]);
const editor = (p: Principal) => p.roles.some((r) => r !== "client_viewer");
const iso = (d: Date) => d.toISOString().slice(0, 10);

/** The UTC start of the period `at` falls in. */
function periodStart(at: Date, cadence: Cadence): Date {
  const d = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  if (cadence === "daily") return d;
  if (cadence === "weekly") return new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY);
  if (cadence === "monthly") return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  if (cadence === "quarterly")
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - (d.getUTCMonth() % 3), 1));
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
}
const before = (start: Date) => new Date(start.getTime() - 1);

const slug = (name: string) =>
  `tasks.${
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 50) || "metric"
  }`;

export function createBoardNumberHandlers(d: BoardNumberDeps): Partial<Record<RouteId, Handler>> {
  let seq = 0;
  const numbers = new Map<string, Stored[]>();
  const listOf = (b: string) => {
    const l = numbers.get(b) ?? [];
    numbers.set(b, l);
    return l;
  };
  const boardOf = (co: string, id: string | undefined) =>
    d.boardsOf(co).find((b) => b.id === id) ?? null;

  function dayOf(t: TaskLite, q: Tile["task_query"]): string | null {
    const v =
      q.date === "field"
        ? (t.fields[q.date_field_id ?? ""] as string | undefined)
        : q.date === "completed_at"
          ? t.completed_at
          : q.date === "due_at"
            ? t.due_at
            : t.created_at;
    return v ? String(v).slice(0, 10) : null;
  }

  function matches(t: TaskLite, q: Tile["task_query"]) {
    const f = q.filter ?? {};
    if (f.statuses && !f.statuses.includes(t.status)) return false;
    if (f.done === true && !t.completed_at) return false;
    if (f.done === false && t.completed_at) return false;
    if (f.field && JSON.stringify(t.fields[f.field.field_id]) !== JSON.stringify(f.field.equals))
      return false;
    return true;
  }

  function measured(q: Tile["task_query"], tasks: TaskLite[]): number | null {
    if (q.measure === "count") return tasks.length;
    const nums = tasks
      .map((t) => t.fields[q.field_id ?? ""])
      .map((v) => (typeof v === "boolean" ? (v ? 1 : 0) : typeof v === "number" ? v : null))
      .filter((v): v is number => v !== null);
    const sum = nums.reduce((s, v) => s + v, 0);
    if (q.measure === "sum") return Math.round(sum * 10_000) / 10_000;
    return nums.length ? Math.round((sum / nums.length) * 10_000) / 10_000 : null;
  }

  function view(p: Principal, board: BoardLite): Numbers {
    const co = p.company.id;
    const now = d.now();
    const tasks = d.tasksOf(co).filter((t) => t.board_id === board.id && !t.deleted);
    const weekStart = periodStart(now, "weekly");
    const weekEnd = new Date(weekStart.getTime() + 7 * DAY);
    const open = tasks.filter((t) => !t.completed_at);
    const can = editor(p);
    const fields = d.fieldsOf(co);
    const throughput = Array.from({ length: 8 }, (_, i) => {
      const start = new Date(weekStart.getTime() - (7 - i) * 7 * DAY);
      const end = new Date(start.getTime() + 7 * DAY);
      return {
        starts_on: iso(start),
        done: tasks.filter((t) => {
          const at = t.completed_at ? Date.parse(t.completed_at) : Number.NaN;
          return at >= start.getTime() && at < end.getTime();
        }).length,
      };
    });
    return {
      board_id: board.id,
      today: iso(now),
      counts: {
        total: tasks.length,
        open: open.length,
        done: tasks.length - open.length,
        overdue: open.filter((t) => t.due_at && Date.parse(t.due_at) < now.getTime()).length,
        due_this_week: open.filter((t) => {
          const at = t.due_at ? Date.parse(t.due_at) : Number.NaN;
          return at >= now.getTime() && at < weekEnd.getTime();
        }).length,
      },
      by_status: board.status_set.map((s) => ({
        key: s.key,
        name: s.name,
        done: s.done === true,
        count: tasks.filter((t) => t.status === s.key).length,
      })),
      throughput,
      tiles: listOf(board.id).map((m) => {
        const points: Tile["trend"] = [];
        let start = periodStart(now, m.cadence);
        for (let i = 0; i < 8; i++) {
          const next = new Date(
            m.cadence === "daily"
              ? start.getTime() + DAY
              : m.cadence === "weekly"
                ? start.getTime() + 7 * DAY
                : Date.UTC(
                    start.getUTCFullYear() + (m.cadence === "yearly" ? 1 : 0),
                    start.getUTCMonth() +
                      (m.cadence === "monthly" ? 1 : m.cadence === "quarterly" ? 3 : 0),
                    1,
                  ),
          );
          // as the server: only periods the 90-day window covers whole, the current one always
          if (
            i > 0 &&
            start.getTime() < periodStart(new Date(now.getTime() - 89 * DAY), m.cadence).getTime()
          )
            break;
          const from = iso(start);
          const to = iso(next);
          const today = iso(now);
          const inPeriod = tasks.filter((t) => {
            const day = dayOf(t, m.task_query);
            return (
              day !== null && day >= from && day < to && day <= today && matches(t, m.task_query)
            );
          });
          points.unshift({
            period_key: from,
            starts_on: from,
            value: measured(m.task_query, inPeriod),
          });
          start = periodStart(before(start), m.cadence);
        }
        const field = fields.find((f) => f.id === m.task_query.field_id);
        const what = m.task_query.measure === "count" ? "tasks" : `${field?.name ?? "a number"}`;
        return {
          metric_def_id: m.id,
          key: m.key,
          name: m.name,
          unit: m.unit,
          direction: m.direction,
          cadence: m.cadence,
          task_query: m.task_query,
          how: `${m.task_query.measure === "count" ? "Counts" : m.task_query.measure === "sum" ? "Adds up" : "Averages"} ${what}, ${m.cadence}.`,
          current: points.at(-1)?.value ?? null,
          previous: points.length > 1 ? (points.at(-2)?.value ?? null) : null,
          trend: points,
          computed_at: now.toISOString(),
          seat_count: 0,
          can_remove: can,
        };
      }),
      proposals: [],
      can_add: can,
    };
  }

  return {
    "boards.numbers": ({ params, principal: p }) => {
      const b = p ? boardOf(p.company.id, params.id) : null;
      if (!p || !b) return notFound();
      // as the work routes: a client viewer is not a work member
      if (!editor(p)) return problem(403, "forbidden", "Forbidden");
      return json(view(p, b));
    },
    "boards.addNumber": ({ params, body, principal: p }) => {
      const b = p ? boardOf(p.company.id, params.id) : null;
      if (!p || !b) return notFound();
      if (!editor(p))
        return problem(403, "forbidden", "Forbidden", "only someone who edits the board");
      const c = (body ?? {}) as Create;
      const name = String(c.name ?? "").trim();
      if (!name) return validation("name is required");
      const q = c.task_query;
      if (!q || q.board_id !== b.id) return validation("a board number counts this board's tasks");
      if (q.measure !== "count") {
        const f = d.fieldsOf(p.company.id).find((x) => x.id === q.field_id);
        if (!f || f.board_id !== b.id || !NUMERIC.has(f.type))
          return validation("pick a number field of the board");
      }
      const taken = new Set([...numbers.values()].flat().map((m) => m.key));
      const base = slug(name);
      let key = base;
      for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
      listOf(b.id).push({
        id: `00000000-0000-4000-8000-${String(++seq).padStart(12, "0")}`,
        key,
        name,
        unit: c.unit ?? "number",
        direction: c.direction ?? "gte",
        cadence: c.cadence ?? "weekly",
        task_query: {
          board_id: q.board_id,
          measure: q.measure,
          field_id: q.field_id ?? null,
          date: q.date,
          date_field_id: q.date_field_id ?? null,
          filter: q.filter ?? {},
        },
      });
      return json(view(p, b), 201);
    },
    "boards.removeNumber": ({ params, principal: p }) => {
      const b = p ? boardOf(p.company.id, params.id) : null;
      if (!p || !b) return notFound();
      if (!editor(p))
        return problem(403, "forbidden", "Forbidden", "only someone who edits the board");
      const l = listOf(b.id);
      const i = l.findIndex((m) => m.id === params.metric_id);
      if (i < 0) return notFound();
      l.splice(i, 1);
      return json(view(p, b));
    },
  };
}
